import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  PaymentSetting,
  CreatePaymentSettingDto,
  UpdatePaymentSettingDto,
} from 'types/paymentSetting';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchProviders } from 'store/paymentProvidersSlice';
import { RootState } from 'store/index';
import { Alert } from 'components/alert/alert';
import styles from './CreateEditPaymentSettingModal.module.scss';

interface CreateEditPaymentSettingModalProps {
  open: boolean;
  siteId: string | number;
  onClose: () => void;
  onSave: (data: CreatePaymentSettingDto | UpdatePaymentSettingDto) => void;
  initialData?: PaymentSetting | null;
  isLoading?: boolean;
  error?: string | null;
}

const CreateEditPaymentSettingModal: React.FC<CreateEditPaymentSettingModalProps> = ({
  open,
  siteId,
  onClose,
  onSave,
  initialData,
  isLoading = false,
  error,
}) => {
  const dispatch = useAppDispatch();
  const { data: providers } = useAppSelector((state: RootState) => state.paymentProviders);

  const [paymentCode, setPaymentCode] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [callbackSecret, setCallbackSecret] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [maxWithdrawAmount, setMaxWithdrawAmount] = useState('');
  const [type, setType] = useState('deposit');

  // Fetch providers when modal opens if not already loaded
  useEffect(() => {
    if (open && providers.length === 0) {
      dispatch(fetchProviders());
    }
  }, [open, dispatch, providers.length]);

  useEffect(() => {
    if (initialData) {
      setPaymentCode(initialData.paymentCode || '');
      setApiKey(initialData.apiKey || '');
      setCallbackSecret(initialData.callbackSecret || '');
      setApiBaseUrl(initialData.apiBaseUrl || '');
      setMaxWithdrawAmount(initialData.maxWithdrawAmount?.toString() || '');
      setType(initialData.type?.toLowerCase() || 'deposit');
    } else {
      setPaymentCode('');
      setApiKey('');
      setCallbackSecret('');
      setApiBaseUrl('');
      setMaxWithdrawAmount('');
      setType('deposit');
    }
  }, [initialData, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (initialData) {
      // Edit mode (typically patch allows partial)
      onSave({
        paymentCode,
        apiKey,
        callbackSecret,
        apiBaseUrl,
        type,
        maxWithdrawAmount: maxWithdrawAmount || undefined,
      });
    } else {
      // Create mode requires siteId included
      onSave({ siteId, paymentCode, apiKey, callbackSecret, apiBaseUrl, type });
    }
  };

  const isEditMode = !!initialData;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: styles.dialogPaper }}
      disableRestoreFocus
      maxWidth="sm"
      fullWidth
    >
      <Typography variant="h6" className={styles.title}>
        {isEditMode ? 'Edit Payment Setting' : 'Create Payment Setting'}
      </Typography>

      {error && <Alert type="error" message={error} className={styles.modalAlert} />}

      <form onSubmit={handleSubmit} className={styles.form}>
        <TextField
          select
          fullWidth
          size="small"
          label="Payment Provider"
          value={paymentCode}
          onChange={(e) => setPaymentCode(e.target.value)}
          className={styles.input}
          required
          disabled={isLoading}
        >
          {providers.map((provider) => (
            <MenuItem key={provider.code} value={provider.code}>
              {provider.name} ({provider.code})
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          size="small"
          label="API Key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          className={styles.input}
          required
          disabled={isLoading}
        />

        <TextField
          fullWidth
          size="small"
          label="Callback Secret"
          value={callbackSecret}
          onChange={(e) => setCallbackSecret(e.target.value)}
          className={styles.input}
          required
          disabled={isLoading}
        />

        <TextField
          fullWidth
          size="small"
          label="API Base URL"
          value={apiBaseUrl}
          onChange={(e) => setApiBaseUrl(e.target.value)}
          className={styles.input}
          required
          disabled={isLoading}
        />

        {(type === 'withdraw' || type === 'both') && (
          <TextField
            fullWidth
            size="small"
            label="Max Withdraw Amount"
            value={maxWithdrawAmount}
            onChange={(e) => setMaxWithdrawAmount(e.target.value.replace(/[^0-9]/g, ''))}
            className={styles.input}
            disabled={isLoading}
          />
        )}

        <FormControl component="fieldset" className={styles.input}>
          <FormLabel component="legend" className={styles.formLabel}>
            Type
          </FormLabel>
          <RadioGroup
            row
            value={type}
            onChange={(e) => setType(e.target.value)}
            className={styles.radioGroup}
          >
            <FormControlLabel
              value="deposit"
              control={<Radio size="small" />}
              label="Deposit"
              disabled={isLoading}
            />
            <FormControlLabel
              value="withdraw"
              control={<Radio size="small" />}
              label="Withdraw"
              disabled={isLoading}
            />
            <FormControlLabel
              value="both"
              control={<Radio size="small" />}
              label="Both"
              disabled={isLoading}
            />
          </RadioGroup>
        </FormControl>

        <Box className={styles.actions}>
          <Button onClick={onClose} className={styles.cancelBtn} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            className={styles.submitBtn}
            disabled={!paymentCode || !apiKey || !callbackSecret || !apiBaseUrl || isLoading}
          >
            {isEditMode ? 'Save' : 'Create'}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default CreateEditPaymentSettingModal;
